import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import PropTypes from 'prop-types';

import { injectPdfjsCdn } from '../../utils';

import Loader from './Loader';

import { useAlerts } from '../context';

pdfjs.GlobalWorkerOptions.workerSrc = injectPdfjsCdn(pdfjs.version);

// eslint-disable-next-line react/require-default-props
function DocumentPreviewModal({ show, data, onHide, modal = true }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const containerRef = useRef(null);

  const nextPage = () => {
    setPageNumber((prevPageNumber) => (prevPageNumber % numPages) + 1);
  };

  const prevPage = () => {
    setPageNumber((prevPageNumber) => ((prevPageNumber - 2 + numPages) % numPages) + 1);
  };

  const options = useMemo(
    () => ({
      cMapUrl: 'cmaps/',
      cMapPacked: true,
    }),
    []
  );

  const footerContent = (
    <>
      <Button variant="outline-light" className="text-meta" onClick={() => prevPage()}>
        <i className="sila-icon arrow-left" />
      </Button>
      {numPages > 1 && (
        <div className="text-center">
          Page {pageNumber} of {numPages}
        </div>
      )}
      <Button variant="outline-light" className="text-meta" onClick={() => nextPage()}>
        <i className="sila-icon arrow-right" />
      </Button>
    </>
  );

  useEffect(() => {
    if (data && data.file_type === 'pdf') {
      setDimensions({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    }
  }, [data]);

  if (!data) return <Loader />;

  if (!modal)
    return (
      <section className="d-flex flex-column flex-grow-1">
        <div
          className="d-flex flex-column flex-grow-1 overflow-auto custom-scrollbar bg-secondary"
          ref={containerRef}
          style={{ height: 500 }}
        >
          <div className="m-auto">
            <Document
              className="loaded"
              loading={<Loader />}
              file={{ url: data.file }}
              options={options}
              // eslint-disable-next-line no-shadow
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={(error) => setAlert({ message: error, variant: 'danger' })}
              onSourceError={(error) => setAlert({ message: error, variant: 'danger' })}
            >
              <Page
                pageNumber={pageNumber}
                width={dimensions.width}
                height={dimensions.height}
                scale={0.99}
              />
            </Document>
          </div>
        </div>
        {numPages > 1 && (
          <footer className="d-flex justify-content-between loaded mt-auto p-4 border-top align-items-center">
            {footerContent}
          </footer>
        )}
      </section>
    );

  return (
    <Modal
      id="doc-preview-modal"
      centered
      backdrop="static"
      show={show}
      size="lg"
      onHide={() => onHide()}
      aria-labelledby="doc-preview-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="doc-preview-modal-title">
          <span className="me-3">{t('common:upload.preview.title')}</span>
          {data && (
            <span className="d-inline-block text-break pe-3 loaded" style={{ maxWidth: '100%' }}>
              {data.file_name}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body ref={containerRef} className="p-0 overflow-auto" style={{ height: 500 }}>
        {data.file_type === 'pdf' ? (
          <Document
            className="loaded"
            loading={<Loader />}
            file={{ url: data.file }}
            options={options}
            // eslint-disable-next-line no-shadow
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={(error) => setAlert({ message: error, variant: 'danger' })}
            onSourceError={(error) => setAlert({ message: error, variant: 'danger' })}
          >
            <Page
              pageNumber={pageNumber}
              width={dimensions.width}
              heihgt={dimensions.height}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        ) : (
          <div className="d-flex justify-content-center align-items-center loaded">
            <img style={{ maxWidth: '100%' }} src={data.file} alt="loaded document" />
          </div>
        )}
      </Modal.Body>
      {numPages > 1 && (
        <Modal.Footer className="d-flex justify-content-between loaded">
          {footerContent}
        </Modal.Footer>
      )}
    </Modal>
  );
}

DocumentPreviewModal.propTypes = {
  /**
   * The visibility boolean
   */
  show: PropTypes.bool.isRequired,
  /**
   * The data object
   */
  // eslint-disable-next-line react/require-default-props, react/forbid-prop-types
  data: PropTypes.object,
  /**
   * The hide function to close the modal
   */
  onHide: PropTypes.func.isRequired,
};

export default DocumentPreviewModal;
